import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { useContext } from 'react';
import SectionWrapper from '@/home/common/SectionWrapper';
import VehicleCarousel from '@/home/common/VehicleCarousel';
import LoadingVehicleCarousel from '@/home/common/VehicleCarousel/LoadingVehicleCarousel';
import VehicleTile from '@/home/common/VehicleCarousel/VehicleTile';
import Sections from '~/constants/home/sections';
import { VdpCardType } from '~/constants/home/VdpCardType';
import { useFavoritesIds } from '~/context/common/UserContext';
import AvailabilityAndPriceContext from '~/context/home/AvailabilityAndPriceContext';
import GlobalContext from '~/context/home/GlobalContext';
import SuggestedForYouContext from '~/context/home/SuggestedForYouContext';
import { useToggleFavorite } from '~/hooks/common/favorites';
import useTileSharedProps from '~/hooks/home/useTileSharedProps';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';

function SuggestedForYou() {
  const { isVdpLinksLoaded, vdpCardTypeShown } = useContext(GlobalContext);
  const {
    suggestedVehicles: {
      hasError: suggestedVehiclesError,
      data: suggestedVehicles,
      isLoading
    }
  } = useContext(SuggestedForYouContext);
  const {
    pageAvailability: { data: availabilityData },
    pagePricing: { data: pricingData }
  } = useContext(AvailabilityAndPriceContext);
  const isSuggestedInVdpLinks = vdpCardTypeShown === VdpCardType.Suggested;
  const { trackEvent } = useContext(AnalyticsContext);
  const analyticsSection = isSuggestedInVdpLinks
    ? 'More suggested vehicles'
    : 'Suggested for you';
  const favoriteIds = useFavoritesIds();
  const toggleFavorite = useToggleFavorite();
  const sharedProps = useTileSharedProps();

  const vehiclesToDisplay = isSuggestedInVdpLinks
    ? suggestedVehicles.slice(3)
    : suggestedVehicles;

  if (isLoading || !isVdpLinksLoaded) {
    return (
      <SectionWrapper analyticsSection={analyticsSection}>
        <LoadingVehicleCarousel hasBottomBorder />
      </SectionWrapper>
    );
  }

  if (suggestedVehiclesError || vehiclesToDisplay.length < 1) return null;

  function sendClickEvent(vehicleId: number) {
    trackEvent('Home feed card', {
      action: 'clicked',
      description: 'Suggested for you tile',
      vehicleId
    });
  }

  return (
    <SectionWrapper analyticsSection={analyticsSection}>
      <VehicleCarousel
        hasBottomBorder
        headerTitle={
          isSuggestedInVdpLinks
            ? 'More suggested vehicles'
            : 'Suggested for you'
        }
      >
        {vehiclesToDisplay.map(vehicle => {
          const { vehicleId } = vehicle;

          const tileAvailability = availabilityData[vehicleId];

          return (
            <VehicleTile
              availabilityInfo={{
                pickup: tileAvailability?.soonestPickup?.content ?? undefined,
                delivery:
                  tileAvailability?.soonestDelivery?.content ?? undefined
              }}
              favoriteIds={favoriteIds}
              financeInfo={pricingData[vehicleId] ?? undefined}
              key={vehicleId}
              vehicle={vehicle}
              sectionTitle="Suggested for you"
              sharedProps={sharedProps}
              toggleFavorite={toggleFavorite}
              handleTileClick={() => sendClickEvent(vehicleId)}
            />
          );
        })}
      </VehicleCarousel>
    </SectionWrapper>
  );
}

export default withErrorBoundary(SuggestedForYou, {
  section: Sections.SuggestedForYou
});
